import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Send, 
  Check, 
  X, 
  RefreshCw, 
  Trash2, 
  Eye, 
  Facebook,
  Instagram,
  Clock,
  CheckCircle,
  XCircle,
  Sparkles,
  ExternalLink,
  Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending_approval: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  posted: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const statusIcons = {
  pending_approval: Clock,
  approved: CheckCircle,
  posted: Send,
  rejected: XCircle,
};

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
};

export default function AdminSocialMedia() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({
    product_id: '',
    platform: 'facebook',
    content: '',
    use_ai: true
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPosts();
    fetchProducts();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/social-posts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(res.data.posts || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error('Failed to load social posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to load products');
    }
  };

  const handleCreate = async () => {
    if (!form.product_id) {
      toast.error('Please select a product');
      return;
    }

    try {
      await axios.post(`${API}/admin/social-posts`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Social post created! Review and approve before publishing.');
      setShowCreateModal(false);
      setForm({ product_id: '', platform: 'facebook', content: '', use_ai: true });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleApprove = async (postId) => {
    try {
      await axios.post(`${API}/admin/social-posts/${postId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post approved! Ready for publishing.');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve post');
    }
  };

  const handleReject = async (postId) => {
    try {
      await axios.post(`${API}/admin/social-posts/${postId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post rejected');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject post');
    }
  };

  const handlePublish = async (postId) => {
    try {
      const res = await axios.post(`${API}/admin/social-posts/${postId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish post');
    }
  };

  const handleRegenerate = async (postId) => {
    try {
      const res = await axios.post(`${API}/admin/social-posts/${postId}/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content regenerated');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate content');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/admin/social-posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleUpdateContent = async (postId, newContent) => {
    try {
      await axios.put(`${API}/admin/social-posts/${postId}`, 
        { content: newContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content updated');
      fetchPosts();
      setShowPreviewModal(false);
    } catch (err) {
      toast.error('Failed to update content');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="social-media-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Social Media</h1>
            <p className="text-muted-foreground">Manage and schedule social media posts for your products</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#78BE20] hover:bg-[#78BE20]/90" data-testid="create-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total || 0}</div>
              <div className="text-sm text-muted-foreground">Total Posts</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-400">{stats.pending_approval || 0}</div>
              <div className="text-sm text-yellow-400/70">Pending Approval</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.approved || 0}</div>
              <div className="text-sm text-blue-400/70">Approved</div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-400">{stats.posted || 0}</div>
              <div className="text-sm text-green-400/70">Posted</div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-400">{stats.rejected || 0}</div>
              <div className="text-sm text-red-400/70">Rejected</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'pending_approval', 'approved', 'posted', 'rejected'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-[#78BE20]' : ''}
            >
              {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Button>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : posts.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Social Posts Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first social media post to promote your products</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-[#78BE20]">
                <Plus className="w-4 h-4 mr-2" />
                Create Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => {
              const StatusIcon = statusIcons[post.status] || Clock;
              const PlatformIcon = platformIcons[post.platform] || Facebook;
              
              return (
                <Card key={post.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`social-post-${post.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                        {post.product_image ? (
                          <img src={post.product_image} alt={post.product_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-zinc-600" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <PlatformIcon className="w-4 h-4 text-blue-400" />
                              <span className="font-medium">{post.product_name}</span>
                              {post.ai_generated && (
                                <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  AI Generated
                                </Badge>
                              )}
                            </div>
                            <Badge className={`${statusColors[post.status]} border`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {post.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedPost(post); setShowPreviewModal(true); }}
                              data-testid={`preview-${post.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {post.status === 'pending_approval' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                                  onClick={() => handleApprove(post.id)}
                                  data-testid={`approve-${post.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                                  onClick={() => handleReject(post.id)}
                                  data-testid={`reject-${post.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRegenerate(post.id)}
                                  data-testid={`regenerate-${post.id}`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {post.status === 'approved' && (
                              <Button
                                size="sm"
                                className="bg-[#78BE20] hover:bg-[#78BE20]/90"
                                onClick={() => handlePublish(post.id)}
                                data-testid={`publish-${post.id}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Publish
                              </Button>
                            )}
                            
                            {post.status === 'posted' && post.post_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(post.post_url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                              onClick={() => handleDelete(post.id)}
                              data-testid={`delete-${post.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Preview Content */}
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2 whitespace-pre-wrap">
                          {post.content}
                        </p>

                        {/* Meta */}
                        <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Created: {formatDate(post.created_at)}</span>
                          {post.approved_at && <span>Approved: {formatDate(post.approved_at)}</span>}
                          {post.posted_at && <span>Posted: {formatDate(post.posted_at)}</span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Social Media Post</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Product *</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="facebook">
                      <div className="flex items-center gap-2">
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </div>
                    </SelectItem>
                    <SelectItem value="instagram">
                      <div className="flex items-center gap-2">
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="use_ai"
                  checked={form.use_ai}
                  onChange={(e) => setForm({ ...form, use_ai: e.target.checked })}
                  className="rounded border-zinc-700"
                />
                <Label htmlFor="use_ai" className="cursor-pointer flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Auto-generate content with AI
                </Label>
              </div>

              {!form.use_ai && (
                <div>
                  <Label>Content</Label>
                  <Textarea
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="Write your post content..."
                    className="bg-zinc-800 border-zinc-700 min-h-[120px]"
                  />
                </div>
              )}

              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-sm text-yellow-400">
                <strong>Note:</strong> Posts will be created as "Pending Approval". You can review and edit the content before publishing.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#78BE20]">
                <Sparkles className="w-4 h-4 mr-2" />
                Create Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview/Edit Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedPost?.platform === 'facebook' ? <Facebook className="w-5 h-5 text-blue-400" /> : <Instagram className="w-5 h-5 text-pink-400" />}
                {selectedPost?.product_name}
              </DialogTitle>
            </DialogHeader>
            {selectedPost && (
              <div className="space-y-4">
                <div className="flex gap-4">
                  {selectedPost.product_image && (
                    <img 
                      src={selectedPost.product_image} 
                      alt={selectedPost.product_name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <Badge className={`${statusColors[selectedPost.status]} border mb-2`}>
                      {selectedPost.status.replace('_', ' ')}
                    </Badge>
                    <Textarea
                      value={selectedPost.content}
                      onChange={(e) => setSelectedPost({ ...selectedPost, content: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 min-h-[200px] font-mono text-sm"
                      disabled={selectedPost.status === 'posted'}
                    />
                  </div>
                </div>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Created: {formatDate(selectedPost.created_at)} by {selectedPost.created_by}</div>
                  {selectedPost.approved_at && <div>Approved: {formatDate(selectedPost.approved_at)} by {selectedPost.approved_by}</div>}
                  {selectedPost.posted_at && <div>Posted: {formatDate(selectedPost.posted_at)}</div>}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Close</Button>
              {selectedPost?.status !== 'posted' && (
                <Button 
                  onClick={() => handleUpdateContent(selectedPost.id, selectedPost.content)}
                  className="bg-[#78BE20]"
                >
                  Save Changes
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
